import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { StoredPhoneField } from "@/components/PhoneNumberInput";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { normalizePhone } from "@/lib/countryDialCodes";
import {
  isPhoneConfirmSnoozed,
  needsCountryCode,
  shouldSkipPhoneConfirm,
  snoozePhoneConfirm,
} from "@/lib/phoneConfirm";

// One-time nudge for accounts whose WhatsApp number was saved by the old
// free-text field without a country code. "8888 7777" could be Costa Rica or
// a number abroad, so the person confirms the country once (the tenant's is
// preselected). New saves always carry a code, so the dialog retires itself.
const PhoneCountryConfirmDialog = () => {
  const { user, loading } = useAuth();
  const tenant = useTenant();
  const { pathname } = useLocation();
  const [stored, setStored] = useState("");
  const [phone, setPhone] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading || !user) {
      setOpen(false);
      return;
    }
    if (isPhoneConfirmSnoozed()) return;
    let cancelled = false;
    supabase.rpc("get_own_phone").then(({ data }) => {
      if (cancelled) return;
      const value = (data as string | null) ?? "";
      if (!needsCountryCode(value)) return;
      setStored(value.trim());
      setPhone(normalizePhone(value, tenant.country));
      setOpen(true);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, loading]);

  if (!user || !open || shouldSkipPhoneConfirm(pathname)) return null;

  const later = () => {
    snoozePhoneConfirm();
    setOpen(false);
  };

  const save = async () => {
    if (!phone) return;
    setSaving(true);
    // Phone-only upsert: including other profile_contacts columns would null them.
    const { error } = await supabase
      .from("profile_contacts")
      .upsert({ user_id: user.id, phone }, { onConflict: "user_id" });
    setSaving(false);
    if (error) {
      toast.error("Couldn't save your number, please try again.");
      return;
    }
    toast.success("WhatsApp number saved");
    setOpen(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && later()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-secondary flex items-center justify-center">
            <MessageCircle className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center font-serif text-xl">Confirm your WhatsApp number</DialogTitle>
          <DialogDescription className="text-center">
            We now save numbers with their country code so people can reach you on WhatsApp. Yours is saved as{" "}
            <strong className="text-foreground">{stored}</strong>, check the country and save.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-1">
          <Label htmlFor="phone-confirm" className="text-sm font-medium">
            WhatsApp number
          </Label>
          <StoredPhoneField id="phone-confirm" value={phone} onChange={setPhone} />
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" className="rounded-full" onClick={later}>
            Not now
          </Button>
          <Button type="button" className="rounded-full" onClick={save} disabled={saving || !phone}>
            {saving ? "Saving…" : "Save number"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneCountryConfirmDialog;
